import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getProfile, getCurrentUser, getUserEnrollments } from '@/lib/api';
import { ProductId, PRODUCT_CONFIGS } from '@/contexts/ProductContext';

/**
 * Redirects users from non-prefixed routes (e.g., /dashboard) 
 * to their primary product's prefixed route (e.g., /mindcotine/dashboard)
 * Also verifies user has enrollment for the requested product
 */
export const ProductRedirect = () => {
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const determineRedirect = async () => {
      try {
        const userResult = await getCurrentUser();
        if (!userResult.success || !userResult.data) {
          setRedirectPath('/auth');
          return;
        }

        // Get user enrollments
        const enrollmentsResult = await getUserEnrollments(userResult.data.id);
        const enrolledProductIds = enrollmentsResult.success 
          ? enrollmentsResult.data.map(e => e.product_id)
          : [];

        // Check if user is trying to access a specific product route
        const pathMatch = location.pathname.match(/^\/(mindcotine|relief)/);
        if (pathMatch) {
          const requestedProduct = pathMatch[1];
          // Verify enrollment
          if (enrolledProductIds.length > 0 && !enrolledProductIds.includes(requestedProduct)) {
            // User not enrolled in this product, redirect to their enrolled product
            const firstEnrolled = enrolledProductIds[0];
            const config = PRODUCT_CONFIGS[firstEnrolled as ProductId];
            if (config) {
              const cleanPath = location.pathname.replace(/^\/(mindcotine|relief)/, '');
              setRedirectPath(`${config.routing.pathPrefix}${cleanPath || '/dashboard'}`);
              return;
            }
          }
        }

        // Get primary product from profile
        const profileResult = await getProfile(userResult.data.id);
        let productId = ProductId.MINDCOTINE;
        
        if (profileResult.success && profileResult.data?.primary_product_id) {
          productId = profileResult.data.primary_product_id as ProductId;
        } else if (enrolledProductIds.length > 0) {
          productId = enrolledProductIds[0] as ProductId;
        }

        const currentPath = location.pathname;
        const productPrefix = `/${productId}`;
        const cleanPath = currentPath.replace(/^\/(mindcotine|relief)/, '');
        const newPath = `${productPrefix}${cleanPath || '/dashboard'}`;
        
        setRedirectPath(newPath);
      } catch (error) {
        console.error('Error determining product redirect:', error);
        setRedirectPath(`/mindcotine${location.pathname}`);
      } finally {
        setLoading(false);
      }
    };

    determineRedirect();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return null;
};